import { useEffect, useRef } from "react";

// Refresh this far ahead of expiry so a request that starts just before the
// deadline still carries a token that is valid when it lands.
const REFRESH_LEAD_SECONDS = 60;

/**
 * Sole scheduler for the background session refresh loop. A successful refresh
 * publishes a new session, which re-runs this and arms the next timer, so the
 * cleanup below is what keeps exactly one timer alive: it cancels the pending
 * one before any replacement is armed, and on unmount.
 *
 * A session already inside the lead window — or expired outright, as after the
 * machine sleeps — clamps to zero and refreshes immediately.
 */
export const useSessionRefreshTimer = (
  userSession: any,
  onRefresh: (session: any) => void
) => {
  // Callers rebuild onRefresh every render. Reading it through a ref keeps it out
  // of the dependencies, so an unrelated re-render of the provider does not tear
  // the timer down and re-arm it for the same deadline.
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (!userSession) {
      return;
    }
    const expiresAt = userSession.idToken.payload.exp;
    const secondsUntilRefresh =
      expiresAt - Math.floor(Date.now() / 1000) - REFRESH_LEAD_SECONDS;
    const timer = setTimeout(() => {
      onRefreshRef.current(userSession);
    }, Math.max(secondsUntilRefresh, 0) * 1000);
    return () => clearTimeout(timer);
  }, [userSession]);
};
